import {Reference, ReferenceType, RelatedDataTarget, RelatedDataTargetType} from "@/components/RelatedDataResolver";
import {TargetType} from "@/components/TargetSelector";
import {getIvrButtonPath} from "@/tools";
import {User, Fax, Gateway, Group, Ivr, ReferenceRetargetsUser, ReferenceRetargetsIvr, ReferenceRetarget} from "@/api";
import {RelatedDataQuery} from "./queries.generated";

type QueryResult = RelatedDataQuery["relatedData"];

type RelatedDataPhoneButton = QueryResult["phoneButtons"][number];
type RelatedDataSecretary = QueryResult["secretary"][number];
type RelatedDataIvrAction = QueryResult["ivrActions"][number];
type RelatedDataGateway = QueryResult["gateways"][number];
type RelatedDataOverrideCaller = QueryResult["overrideCaller"][number];

// convert all properties to optional
type OptionalProperties<T> = {
    [K in keyof T]?: OptionalProperties<T[K]>;
};

type RawTarget<R extends RelatedDataTargetType> = R extends RelatedDataTargetType.EXTENSION
    ? OptionalProperties<User>
    : R extends RelatedDataTargetType.IVR
      ? OptionalProperties<Ivr>
      : R extends RelatedDataTargetType.GROUP
        ? OptionalProperties<Group>
        : R extends RelatedDataTargetType.FAX
          ? OptionalProperties<Fax>
          : R extends RelatedDataTargetType.GATEWAY
            ? OptionalProperties<Gateway>
            : never;

type TargetTypeString = "users" | "ivrs" | "groups" | "faxes" | "gateways";

export type RawTargetDict = {
    users?: {items: RawTarget<RelatedDataTargetType.EXTENSION>[]};
    ivrs?: {items: RawTarget<RelatedDataTargetType.IVR>[]};
    groups?: {items: RawTarget<RelatedDataTargetType.GROUP>[]};
    faxes?: {items: RawTarget<RelatedDataTargetType.FAX>[]};
    gateways?: {items: RawTarget<RelatedDataTargetType.GATEWAY>[]};
};

const FILTER_TARGET_TYPE_MAP: Record<TargetTypeString, TargetType> = {
    users: TargetType.EXTENSION,
    ivrs: TargetType.IVR,
    groups: TargetType.GROUP,
    faxes: TargetType.FAX,
    gateways: TargetType.GW,
};

export const convertRawToRelatedDataTargets = <R extends RelatedDataTargetType>(
    targets: RawTarget<R>[] | undefined,
    targetType: TargetTypeString,
): RelatedDataTarget[] => {
    return (
        targets?.map((target) => ({
            type: FILTER_TARGET_TYPE_MAP[targetType],
            numberId: target.number.id,
            // displayName: target.__typename === "Fax" ? null : target.displayName ?? target.name,
            displayName:
                targetType === "faxes"
                    ? null
                    : targetType === "ivrs" || targetType === "groups"
                      ? (target as RawTarget<RelatedDataTargetType.IVR>).name
                      : (target as RawTarget<RelatedDataTargetType.EXTENSION>).displayName,
            shortNumber: target.number.shortNumber,
            exclusionValue: target.number.id,
        })) ?? []
    );
};

type Restriction<R extends ReferenceType> = R extends ReferenceType.IVR_ACTION
    ? "users" | "ivrs" | "groups" | "faxes"
    : R extends ReferenceType.GATEWAY
      ? "users" | "ivrs" | "groups"
      : R extends ReferenceType.OVERRIDE_CALLER
        ? "users" | "ivrs" | "groups" | "faxes" | "gateways"
        : never;

export const getTargetsForReferenceType = <R extends ReferenceType>(
    referenceType: R,
    targetsDict: RawTargetDict,
    ...restrictions: Restriction<R>[]
): RelatedDataTarget[] => {
    if (targetsDict === undefined) return [];

    if (restrictions.length > 0)
        return restrictions.reduce(
            (acc, restriction) =>
                acc.concat(convertRawToRelatedDataTargets(targetsDict[restriction].items ?? [], restriction)),
            [] as RelatedDataTarget[],
        );

    switch (referenceType) {
        case ReferenceType.PHONE_BUTTON:
        case ReferenceType.SECRETARY:
            return convertRawToRelatedDataTargets(targetsDict["users"].items, "users");
        case ReferenceType.IVR_ACTION:
            return convertRawToRelatedDataTargets(targetsDict["users"].items, "users")
                .concat(convertRawToRelatedDataTargets(targetsDict["ivrs"].items, "ivrs"))
                .concat(convertRawToRelatedDataTargets(targetsDict["groups"].items, "groups"))
                .concat(convertRawToRelatedDataTargets(targetsDict["faxes"].items, "faxes"));
        case ReferenceType.GATEWAY:
            return convertRawToRelatedDataTargets(targetsDict["users"].items, "users")
                .concat(convertRawToRelatedDataTargets(targetsDict["ivrs"].items, "ivrs"))
                .concat(convertRawToRelatedDataTargets(targetsDict["groups"].items, "groups"));
        case ReferenceType.OVERRIDE_CALLER:
            return convertRawToRelatedDataTargets(targetsDict["users"].items, "users")
                .concat(convertRawToRelatedDataTargets(targetsDict["ivrs"].items, "ivrs"))
                .concat(convertRawToRelatedDataTargets(targetsDict["groups"].items, "groups"))
                .concat(convertRawToRelatedDataTargets(targetsDict["faxes"].items, "faxes"))
                .concat(convertRawToRelatedDataTargets(targetsDict["gateways"].items, "gateways"));
        default:
            return [];
    }
};

const convertRawIvrActionsToReferences = (
    rawReferences: RelatedDataIvrAction[],
    exclusionValue?: any,
): Reference<ReferenceType.IVR_ACTION>[] =>
    rawReferences.map((rawReference): Reference<ReferenceType.IVR_ACTION> => {
        const ivr = rawReference.ivrProfile.mainParent
            ? rawReference.ivrProfile.mainParent.ivr
            : rawReference.ivrProfile.ivr;
        return {
            referrerId: rawReference.id,
            displayName: ivr.name,
            ivrId: ivr.id,
            profileName: null,
            actionPath: getIvrButtonPath(rawReference.ivrProfile.ivr.name, rawReference.button),
            exclusionValues: [ivr.numberId, exclusionValue ?? 0],
        };
    });

const convertRawSecretaryToReferences = (
    rawReferences: RelatedDataSecretary[],
    exclusionValue?: any,
): Reference<ReferenceType.SECRETARY>[] =>
    rawReferences.map((rawReference) => ({
        referrerId: rawReference.id,
        displayName: rawReference.displayName,
        exclusionValues: [rawReference.number.id, exclusionValue ?? 0],
    }));

const convertRawGatewayToReferences = (
    rawReferences: RelatedDataGateway[],
    exclusionValue?: any,
): Reference<ReferenceType.GATEWAY>[] =>
    rawReferences.map((rawReference) => ({
        referrerId: rawReference.id,
        displayName: rawReference.displayName,
        exclusionValues: [rawReference.number.id, exclusionValue ?? 0],
    }));

const convertRawOverrideCallerToReferences = (
    rawReferences: RelatedDataOverrideCaller[],
    exclusionValue?: any,
): Reference<ReferenceType.OVERRIDE_CALLER>[] =>
    rawReferences.map((rawReference) => ({
        referrerId: rawReference.id,
        displayName: rawReference.displayName,
        exclusionValues: [rawReference.number.id, exclusionValue ?? 0],
    }));

const convertRawPhoneButtonToReferences = (
    rawReferences: RelatedDataPhoneButton[],
    exclusionValue?: any,
): Reference<ReferenceType.PHONE_BUTTON>[] =>
    rawReferences.map((rawReference) => ({
        referrerId: rawReference.id,
        displayName: rawReference.device.extension.displayName,
        extensionId: rawReference.device.extensionId,
        button: rawReference.buttonPosition,
        exclusionValues: [rawReference.device.extension.numberId, exclusionValue ?? 0],
    }));

export type ProcessedUserRelatedData = {
    ivrActions: Reference<ReferenceType.IVR_ACTION>[];
    gateways: Reference<ReferenceType.GATEWAY>[];
    phoneButtons: Reference<ReferenceType.PHONE_BUTTON>[];
    secretary: Reference<ReferenceType.SECRETARY>[];
    overrideCaller: Reference<ReferenceType.OVERRIDE_CALLER>[];
};

export const processUserRelatedDataQueryResult = (
    queryResult: QueryResult | undefined,
    exclusionValue?: any,
): ProcessedUserRelatedData => {
    if (queryResult === undefined)
        return {
            ivrActions: [],
            gateways: [],
            phoneButtons: [],
            secretary: [],
            overrideCaller: [],
        };

    return {
        ivrActions: convertRawIvrActionsToReferences(queryResult.ivrActions, exclusionValue),
        gateways: convertRawGatewayToReferences(queryResult.gateways, exclusionValue),
        phoneButtons: convertRawPhoneButtonToReferences(queryResult.phoneButtons, exclusionValue),
        secretary: convertRawSecretaryToReferences(queryResult.secretary, exclusionValue),
        overrideCaller: convertRawOverrideCallerToReferences(queryResult.overrideCaller, exclusionValue),
    };
};

export type RelatedDataProcessedQueryResult<T extends RelatedDataTargetType> = T extends RelatedDataTargetType.EXTENSION
    ? ProcessedUserRelatedData
    : T extends RelatedDataTargetType.IVR
      ? {
            ivrActions: Reference<ReferenceType.IVR_ACTION>[];
            gateways: Reference<ReferenceType.GATEWAY>[];
            overrideCaller: Reference<ReferenceType.OVERRIDE_CALLER>[];
        }
      : T extends RelatedDataTargetType.GROUP
        ? {
              ivrActions: Reference<ReferenceType.IVR_ACTION>[];
              gateways: Reference<ReferenceType.GATEWAY>[];
              overrideCaller: Reference<ReferenceType.OVERRIDE_CALLER>[];
          }
        : T extends RelatedDataTargetType.FAX
          ? {
                ivrActions: Reference<ReferenceType.IVR_ACTION>[];
                overrideCaller: Reference<ReferenceType.OVERRIDE_CALLER>[];
            }
          : T extends RelatedDataTargetType.GATEWAY
            ? {
                  overrideCaller: Reference<ReferenceType.OVERRIDE_CALLER>[];
              }
            : object;

export const processRelatedDataQueryResult = <T extends RelatedDataTargetType>(
    queryResult: QueryResult | undefined,
    targetType: T,
    exclusionValue?: any,
): RelatedDataProcessedQueryResult<T> => {
    switch (targetType) {
        case RelatedDataTargetType.EXTENSION:
            return processUserRelatedDataQueryResult(queryResult, exclusionValue) as RelatedDataProcessedQueryResult<T>;

        case RelatedDataTargetType.IVR:
            if (queryResult === undefined)
                return {
                    ivrActions: [],
                    gateways: [],
                    overrideCaller: [],
                } as RelatedDataProcessedQueryResult<T>;

            return {
                ivrActions: convertRawIvrActionsToReferences(queryResult.ivrActions, exclusionValue),
                gateways: convertRawGatewayToReferences(queryResult.gateways, exclusionValue),
                overrideCaller: convertRawOverrideCallerToReferences(queryResult.overrideCaller, exclusionValue),
            } as RelatedDataProcessedQueryResult<T>;

        case RelatedDataTargetType.GROUP:
            if (queryResult === undefined)
                return {
                    ivrActions: [],
                    gateways: [],
                    overrideCaller: [],
                } as RelatedDataProcessedQueryResult<T>;

            return {
                ivrActions: convertRawIvrActionsToReferences(queryResult.ivrActions, exclusionValue),
                gateways: convertRawGatewayToReferences(queryResult.gateways, exclusionValue),
                overrideCaller: convertRawOverrideCallerToReferences(queryResult.overrideCaller, exclusionValue),
            } as RelatedDataProcessedQueryResult<T>;

        case RelatedDataTargetType.FAX:
            if (queryResult === undefined)
                return {
                    ivrActions: [],
                    overrideCaller: [],
                } as RelatedDataProcessedQueryResult<T>;

            return {
                ivrActions: convertRawIvrActionsToReferences(queryResult.ivrActions, exclusionValue),
                overrideCaller: convertRawOverrideCallerToReferences(queryResult.overrideCaller, exclusionValue),
            } as RelatedDataProcessedQueryResult<T>;

        case RelatedDataTargetType.GATEWAY:
            if (queryResult === undefined)
                return {
                    overrideCaller: [],
                } as RelatedDataProcessedQueryResult<T>;

            return {
                overrideCaller: convertRawOverrideCallerToReferences(queryResult.overrideCaller, exclusionValue),
            } as RelatedDataProcessedQueryResult<T>;

        default:
            return {} as RelatedDataProcessedQueryResult<T>;
    }
};

function createInitialData<T extends {id: number | string}>(
    rawReferences: T[] | undefined,
    defaultTarget: number | undefined = undefined,
): {newTargetNumberId: number; referrerId: T["id"]}[] {
    if (!rawReferences) {
        return [];
    }
    return rawReferences.map((rawReference) => ({
        newTargetNumberId: defaultTarget,
        referrerId: rawReference.id,
    }));
}

/// creates initial related data for deleting a user
export function initialRelatedDataUser(queryResult: QueryResult | undefined): ReferenceRetargetsUser {
    if (queryResult === undefined) return null;

    return {
        ivrActions: createInitialData(queryResult.ivrActions),
        gateways: createInitialData(queryResult.gateways),
        phoneButtons: createInitialData(queryResult.phoneButtons, 0),
        secretary: createInitialData(queryResult.secretary),
        overrideCaller: createInitialData(queryResult.overrideCaller),
    };
}

/// creates initial related data for deleting an auto attendant
export function initialRelatedDataIvr(queryResult: QueryResult | undefined): ReferenceRetargetsIvr {
    if (queryResult === undefined) return null;

    return {
        ivrActions: createInitialData(queryResult.ivrActions),
        gateways: createInitialData(queryResult.gateways),
        overrideCaller: createInitialData(queryResult.overrideCaller),
    };
}

/// returns true if there is no undefined target in the data
export function isResolved(data: Record<string, ReferenceRetarget[]>) {
    return Object.values(data).every((retargets) => {
        return retargets.find((rt) => rt.newTargetNumberId === undefined) === undefined;
    });
}
